import { z } from 'zod';
import type { APICarrierData } from 'types/api.types';
import type { MVRProductType } from 'api';

export type { MVRProductType };

interface GetMvrRequestSchemaOptions {
  requiresPolicyNumber: boolean;
  requiresProductType: boolean;
  requiresQuoteReferenceNumber: boolean;
}

// Zod validation schema for the MVR Request form. Policy number, product
// type, and quote/reference number are only required for transaction types
// that show those fields (see MVR_REQUEST_TYPE_CONFIG).
export const getMvrRequestSchema = ({
  requiresPolicyNumber,
  requiresProductType,
  requiresQuoteReferenceNumber,
}: GetMvrRequestSchemaOptions) =>
  z.object({
    carrier: z
      .any()
      .nullable()
      .refine((val) => !!val, 'Carrier is required'),
    province: z
      .number()
      .nullable()
      .refine((val) => val !== null, 'Province is required'),
    productType: requiresProductType
      ? z
          .any()
          .nullable()
          .refine((val) => !!val, 'Product Type is required')
      : z.any().nullable().optional(),
    policyNumber: requiresPolicyNumber
      ? z.string().min(1, 'Policy number is required')
      : z.string().optional(),
    quoteReferenceNumber: requiresQuoteReferenceNumber
      ? z.string().min(1, 'Quote or Reference number is required')
      : z.string().optional(),
    date: z
      .date()
      .nullable()
      .refine((val) => val !== null, 'Date is required'),
  });

export type MVRRequestFormData = {
  carrier: APICarrierData | null;
  province: number | null;
  productType: MVRProductType | null;
  policyNumber: string;
  quoteReferenceNumber: string;
  date: Date | null;
};

export const initialMvrRequestFormData: MVRRequestFormData = {
  carrier: null,
  province: null,
  productType: null,
  policyNumber: '',
  quoteReferenceNumber: '',
  date: null,
};
